import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import httpService from "../services/httpService";
import posthog from 'posthog-js';
import eventBus from "../services/eventBus";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import {
    FaChevronLeft, FaChevronRight,
    FaSearch, FaReply, FaCommentAlt, FaTimes,
    FaThumbsUp, FaHeart, FaLaughSquint, FaSadTear, FaAngry,
    FaSortAmountUp, FaSortAmountDown,
} from "react-icons/fa";

import LoadingFade from "../components/LoadingFade";
import PageHero from "../components/PageHero";
import UserAvatar from "../components/UserAvatar";
import "../styles/dashbaords.scss";
import "../styles/listenerDiscussion.scss";
import { isFutureDate } from "../utils/podcastClubUtils";

const REACTION_CONFIG = [
    { type: 'like',  Icon: FaThumbsUp,   label: 'Like'  },
    // { type: 'love',  Icon: FaHeart,       label: 'Love'  },
    // { type: 'laugh', Icon: FaLaughSquint, label: 'Laugh' },
    // { type: 'sad',   Icon: FaSadTear,     label: 'Sad'   },
    // { type: 'angry', Icon: FaAngry,       label: 'Angry' },
];

const ListenerDiscussion = () => {
    const { guid, discussionGuid } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading]               = useState(true);
    const [error, setError]                   = useState(null);
    const [comments, setComments]             = useState([]);
    const [discussion, setDiscussion]         = useState(null);
    const [listeners, setListeners]           = useState([]);
    const [podcaster, setPodcaster]           = useState(null);
    const [search, setSearch]                 = useState('');
    const [sort, setSort]                     = useState('asc');
    const [pageNumber, setPageNumber]         = useState(1);
    const [pageSize, setPageSize]             = useState(20);
    const [pagination, setPagination]         = useState({ total: 0, pageNumber: 1, pageSize: 20 });

    const [showCommentModal, setShowCommentModal]     = useState(false);
    const [activeParentComment, setActiveParentComment] = useState(null);
    const [commentBody, setCommentBody]               = useState('');
    const [commentMentions, setCommentMentions]       = useState([]);
    const [mentionQuery, setMentionQuery]             = useState(null);
    const [submittingComment, setSubmittingComment]   = useState(false);
    const [commentError, setCommentError]             = useState(null);
    const [reactions, setReactions]                   = useState({});
    const [reactionCounts, setReactionCounts]         = useState({});
    const textareaRef = useRef(null);

    const [sidebarDiscussions, setSidebarDiscussions] = useState([]);
    const [sidebarEpisodes, setSidebarEpisodes]       = useState([]);

    const fetchComments = (page = pageNumber, sortValue = sort) => {
        const params = { pageNumber: page, pageSize, sort: sortValue, ...(search && { search }) };
        httpService.get(`dash/listener/discussions/${guid}/${discussionGuid}/comments`, { params })
            .then((res) => {
                setComments(res.data.data);
                setDiscussion(res.data.discussion || null);
                setListeners(res.data.podcast_club_listeners || []);
                setPodcaster(res.data.podcaster || null);
                setPagination({ total: res.data.total, pageNumber: res.data.pageNumber, pageSize: res.data.pageSize });
                const reactionMap = {};
                const countMap = {};
                const extract = (list) => list?.forEach((c) => {
                    reactionMap[c.id] = c.my_reaction?.reaction_type || null;
                    countMap[c.id]    = c.reactions || { like: 0, love: 0, laugh: 0, sad: 0, angry: 0 };
                    extract(c.sub_comments);
                });
                extract(res.data.data);
                setReactions(reactionMap);
                setReactionCounts(countMap);
                // eventBus.emit('toast', { type: 'light', message: 'Loading comments' });
            })
            .catch((err) => setError(err.response?.data?.message || 'Error loading comments'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchComments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guid, discussionGuid, pageNumber, pageSize, sort]);

    useEffect(() => {
        httpService.get(`dash/listener-podcast-club/${guid}`)
            .then(res => setSidebarEpisodes(res.data.podcast_club?.episodes || []))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guid]);

    useEffect(() => {
        const episodePivotGuid = discussion?.episode?.guid;
        if (!episodePivotGuid) return;
        httpService.get(`dash/listener/discussions/${guid}/episode/${episodePivotGuid}`)
            .then(res => setSidebarDiscussions(
                (res.data.data || []).filter(d => d.guid !== discussionGuid)
            ))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [discussion?.episode?.guid]);

    const handleSearchSubmit = (e) => { e.preventDefault(); setPageNumber(1); fetchComments(1); };
    const handleClearSearch  = () => { setSearch(''); setPageNumber(1); fetchComments(1); };

    const handleReaction = (commentId, type) => {
        const prevType = reactions[commentId];
        httpService.post(`dash/listener/discussions/comments/${commentId}/reaction`, { reaction_type: type })
            .then((res) => {
                const newType = res.data.reaction?.reaction_type || null;
                setReactions((prev) => ({ ...prev, [commentId]: newType }));
                setReactionCounts((prev) => {
                    const counts = { ...(prev[commentId] || {}) };
                    if (prevType) counts[prevType] = Math.max(0, (counts[prevType] || 0) - 1);
                    if (newType)  counts[newType]  = (counts[newType] || 0) + 1;
                    return { ...prev, [commentId]: counts };
                });
            });
    };

    const totalPages = Math.ceil(pagination.total / pagination.pageSize) || 1;

    // ── Comment modal helpers ───────────────────────────────────────────────────
    const openCommentModal = (parentComment = null) => {
        setActiveParentComment(parentComment);
        setCommentBody('');
        setCommentMentions([]);
        setMentionQuery(null);
        setCommentError(null);
        setShowCommentModal(true);
    };
    const closeCommentModal = () => { setShowCommentModal(false); setActiveParentComment(null); setMentionQuery(null); };

    const handleCommentBodyChange = (e) => {
        const val = e.target.value;
        setCommentBody(val);
        const match = val.slice(0, e.target.selectionStart).match(/@(\w*)$/);
        setMentionQuery(match ? match[1].toLowerCase() : null);
    };

    const handleSelectMention = (listener) => {
        const pos      = textareaRef.current.selectionStart;
        const replaced = commentBody.slice(0, pos).replace(/@(\w*)$/, `@${listener.name} `);
        setCommentBody(replaced + commentBody.slice(pos));
        setCommentMentions((prev) => prev.includes(listener.guid) ? prev : [...prev, listener.guid]);
        setMentionQuery(null);
        setTimeout(() => textareaRef.current?.focus(), 0);
    };

    const handleSubmitComment = () => {
        if (!commentBody.trim()) return;
        setSubmittingComment(true);
        setCommentError(null);
        httpService.post('dash/listener/discussions/comments', {
            discussion_id:   discussion.id,
            body:            commentBody.trim(),
            parent_comment_id: activeParentComment?.id ?? null,
            mentions:        commentMentions.length > 0 ? commentMentions : null,
        })
            .then(() => {
                posthog.capture('discussion_comment_posted', {
                    club_guid: guid, discussion_guid: discussionGuid,
                    is_reply: !!activeParentComment, has_mentions: commentMentions.length > 0,
                });
                closeCommentModal();
                fetchComments(pageNumber);
            })
            .catch((err) => setCommentError(err.response?.data?.message || 'Failed to post comment.'))
            .finally(() => setSubmittingComment(false));
    };

    const filteredListeners = mentionQuery !== null && mentionQuery !== ''
        ? listeners.filter((l) => l.name?.toLowerCase().includes(mentionQuery)).slice(0, 6)
        : [];

    // ── Sub-comment renderer ────────────────────────────────────────────────────
    const renderSubComments = (subComments) => {
        if (!subComments?.length) return null;
        return (
            <div className="ld-sub-comments">
                {subComments.map((sub) => {
                    const isSubPodcaster = podcaster && sub.user?.guid === podcaster.user?.guid;
                    return (
                        <div key={sub.id} className={`ld-sub-comment${isSubPodcaster ? ' ld-sub-comment--podcaster' : ''}`}>
                            <FaReply className="ld-sub-comment__icon" />
                            <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <UserAvatar user={sub.user} size={30} />
                                    <strong className="ld-comment__name">{sub.user?.name}</strong>
                                    {isSubPodcaster && <Badge bg="primary" style={{ fontSize: '0.6rem' }}>Podcaster</Badge>}
                                    <span className="ld-comment__time ms-auto">{new Date(sub.created_at).toLocaleString()}</span>
                                </div>
                                <p className="ld-comment__body mb-2">{sub.body}</p>
                                <div className="ld-comment__actions">
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Reply</Tooltip>}>
                                        <Button variant="outline-primary" size="sm" onClick={() => openCommentModal(sub)}>
                                            <FaCommentAlt />
                                        </Button>
                                    </OverlayTrigger>
                                    <div className="d-flex flex-wrap gap-1">
                                        {REACTION_CONFIG.map(({ type, Icon, label }) => {
                                            const count = reactionCounts[sub.id]?.[type] || 0;
                                            return (
                                                <OverlayTrigger key={type} placement="top" overlay={<Tooltip>{label}</Tooltip>}>
                                                    <Button
                                                        variant={reactions[sub.id] === type ? 'primary' : 'outline-secondary'}
                                                        size="sm"
                                                        onClick={() => handleReaction(sub.id, type)}
                                                    >
                                                        <Icon />{count > 0 && <span className="ms-1">{count}</span>}
                                                    </Button>
                                                </OverlayTrigger>
                                            );
                                        })}
                                    </div>
                                </div>
                                {renderSubComments(sub.sub_comments)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <LoadingFade id="ListenerDiscussion-component" loading={loading} stateKey={loading ? 'loading' : 'loaded'}>

            {/* ── Hero ── */}
            <PageHero backgroundImage="/img/discussion-wide.jpg" minHeight="200px">
                <button className="ld-back-btn btn" onClick={() => navigate(`/listener-podcast-club/${guid}`)}>
                    <FaChevronLeft className="me-1" /> Back to Club
                </button>

                <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap mt-2">
                    <div className="flex-grow-1 overflow-hidden">
                        {discussion?.is_pinned === 1 && (
                            <Badge bg="warning" text="dark" className="mb-3"
                                   style={{ fontSize: '1.5em', verticalAlign: 'middle' }}>
                                Pinned
                            </Badge>
                        )}
                        <h1 className="page-hero__title">
                            {discussion?.title || 'Discussion'}
                        </h1>
                        {discussion?.body && (
                            <p className="page-hero__sub">{discussion.body}</p>
                        )}
                        <div className="d-flex gap-2 mt-2 flex-wrap align-items-center">
                            {/*<Badge*/}
                            {/*    bg={discussion?.status === 'published' ? 'success' : 'secondary'}*/}
                            {/*    style={{ fontSize: '0.72rem' }}*/}
                            {/*>*/}
                            {/*    {discussion?.status}*/}
                            {/*</Badge>*/}
                            {discussion?.user?.name && (
                                <span className="ld-hero-meta">by {discussion.user.name}</span>
                            )}
                            {podcaster && (
                                <span className="ld-hero-meta">· hosted by {podcaster.user?.name}</span>
                            )}
                            {pagination.total > 0 && (
                                <span className="ld-hero-meta">· {pagination.total} comment{pagination.total !== 1 ? 's' : ''}</span>
                            )}
                        </div>
                    </div>

                    {discussion?.allow_comments !== false && (
                        <Button variant="light" size="sm" onClick={() => openCommentModal()} className="flex-shrink-0">
                            <FaCommentAlt className="me-1" /> Add Comment
                        </Button>
                    )}
                </div>
            </PageHero>

            {/* ── Content ── */}
            <div className="container-lg py-4 px-3 px-lg-4">

                {error && <div className="alert alert-danger mb-3">{error}</div>}

                <Row className="g-4">
                <Col lg={8}>
                {discussion?.allow_comments === false ? (
                    <div className="text-center py-5 text-muted">
                        <FaCommentAlt style={{ fontSize: '2.5rem', opacity: 0.12, display: 'block', margin: '0 auto 0.75rem' }} />
                        Comments are disabled for this discussion.
                    </div>
                ) : (
                    <>
                        {/* Toolbar */}
                        <Row className="mb-4 align-items-center g-2">
                            <Col md={7}>
                                <Form onSubmit={handleSearchSubmit}>
                                    <div className="d-flex gap-2">
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="text"
                                                placeholder="Search comments…"
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                            <Button variant="primary" type="submit"><FaSearch /></Button>
                                        </InputGroup>
                                        {search && (
                                            <Button variant="outline-secondary" size="sm" onClick={handleClearSearch}>
                                                <FaTimes />
                                            </Button>
                                        )}
                                        <OverlayTrigger placement="top"
                                            overlay={<Tooltip>{sort === 'asc' ? 'Oldest first' : 'Newest first'}</Tooltip>}>
                                            <Button variant="outline-secondary" size="sm"
                                                onClick={() => setSort(s => s === 'asc' ? 'desc' : 'asc')}>
                                                {sort === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </Form>
                            </Col>
                            <Col md={5} className="d-flex align-items-center justify-content-end gap-2">
                                <span className="text-muted small">Per page:</span>
                                <Form.Select size="sm" value={pageSize}
                                    onChange={(e) => { setPageSize(parseInt(e.target.value)); setPageNumber(1); }}
                                    style={{ width: 'auto' }}>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                </Form.Select>
                            </Col>
                        </Row>

                        {/* Comment list */}
                        {comments.length > 0 ? (
                            <>
                                {comments.map((comment) => {
                                    const isPodcaster = podcaster && comment.user?.guid === podcaster.user?.guid;
                                    return (
                                        <div key={comment.id}
                                            className={`ld-comment${isPodcaster ? ' ld-comment--podcaster' : ''}`}>

                                            {/* Header */}
                                            <div className="ld-comment__header">
                                                <div className="d-flex align-items-center gap-2">
                                                    <UserAvatar user={comment.user} size={42} />
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <strong className="ld-comment__name">{comment.user?.name}</strong>
                                                            {isPodcaster && (
                                                                <Badge bg="primary" style={{ fontSize: '0.62rem' }}>Podcaster</Badge>
                                                            )}
                                                        </div>
                                                        <div className="ld-comment__time">
                                                            {new Date(comment.created_at).toLocaleString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Body */}
                                            <p className="ld-comment__body">{comment.body}</p>

                                            {/* Actions */}
                                            <div className="ld-comment__actions">
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Reply</Tooltip>}>
                                                    <Button variant="outline-primary" size="sm"
                                                        onClick={() => openCommentModal(comment)}>
                                                        <FaCommentAlt />
                                                    </Button>
                                                </OverlayTrigger>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {REACTION_CONFIG.map(({ type, Icon, label }) => {
                                                        const count = reactionCounts[comment.id]?.[type] || 0;
                                                        return (
                                                            <OverlayTrigger key={type} placement="top"
                                                                overlay={<Tooltip>{label}</Tooltip>}>
                                                                <Button
                                                                    variant={reactions[comment.id] === type ? 'primary' : 'outline-secondary'}
                                                                    size="sm"
                                                                    onClick={() => handleReaction(comment.id, type)}
                                                                >
                                                                    <Icon />
                                                                    {count > 0 && <span className="ms-1">{count}</span>}
                                                                </Button>
                                                            </OverlayTrigger>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {renderSubComments(comment.sub_comments)}
                                        </div>
                                    );
                                })}

                                {/* Pagination */}
                                <div className="d-flex align-items-center justify-content-between mt-4">
                                    <small className="text-muted">
                                        {((pageNumber - 1) * pageSize + 1)}–{Math.min(pageNumber * pageSize, pagination.total)} of {pagination.total} comments
                                    </small>
                                    <div className="d-flex align-items-center gap-2">
                                        <Button variant="outline-primary" size="sm"
                                            onClick={() => setPageNumber(p => p - 1)} disabled={pageNumber <= 1}>
                                            <FaChevronLeft />
                                        </Button>
                                        <small>Page {pageNumber} of {totalPages}</small>
                                        <Button variant="outline-primary" size="sm"
                                            onClick={() => setPageNumber(p => p + 1)} disabled={pageNumber >= totalPages}>
                                            <FaChevronRight />
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            !loading && (
                                <div className="text-center py-5 text-muted">
                                    <FaCommentAlt style={{ fontSize: '2.5rem', opacity: 0.12, display: 'block', margin: '0 auto 0.75rem' }} />
                                    No comments yet. Be the first!
                                </div>
                            )
                        )}
                    </>
                )}
                </Col>
                <Col lg={4}>
                    <div className="ep-sidebar">

                        <div className="mb-4">
                            <h6 className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '0.05em' }}>
                                Other Discussions
                            </h6>
                            {sidebarDiscussions.length === 0
                                ? <p className="text-muted small">No other discussions for this episode.</p>
                                : sidebarDiscussions.map(d => (
                                    <div key={d.guid}
                                         className="mb-2 p-2 border rounded"
                                         style={{ cursor: 'pointer' }}
                                         onClick={() => navigate(`/listener-podcast-club/${guid}/discussion/${d.guid}`)}>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <span className="small fw-semibold">{d.title}</span>
                                            {d.is_pinned === 1 && (
                                                <Badge bg="warning" className="ms-1" style={{ fontSize: '0.65rem' }}>Pinned</Badge>
                                            )}
                                        </div>
                                        <small className="text-muted">{d.comments_count ?? 0} comments</small>
                                    </div>
                                ))
                            }
                        </div>

                        <div>
                            <h6 className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '0.05em' }}>
                                Episodes
                            </h6>
                            {sidebarEpisodes.map(ep => {
                                const img     = ep.pivot?.featured_image || ep.featured_image;
                                const title   = ep.pivot?.title || ep.title;
                                const href    = `/listener-episode/${ep.pivot?.podcast_club_id}/${ep.pivot?.podcast_episode_id}`;
                                const upcoming = isFutureDate(ep.pivot?.start_date_time);
                                return (
                                    <div key={ep.id} className="mb-2 p-2 border rounded d-flex gap-2 align-items-start">
                                        {img && (
                                            <img src={img} alt={title}
                                                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                                        )}
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <div className="small fw-semibold text-truncate">{title}</div>
                                            {ep.pivot?.start_date_time && (
                                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                    {upcoming ? 'Starts' : 'Available'}: {new Date(ep.pivot.start_date_time).toLocaleDateString()}
                                                </div>
                                            )}
                                            {!upcoming && (
                                                <a href={href} className="small text-decoration-none">View episode →</a>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                </Col>
                </Row>
            </div>

            {/* ── Comment modal ── */}
            <Modal show={discussion?.allow_comments !== false && showCommentModal}
                onHide={closeCommentModal} centered dialogClassName="ld-comment-modal">
                <Modal.Body className="p-0">

                    {/* Modal header — gradient bar */}
                    <div className="ld-modal__header">
                        <div>
                            <div className="ld-modal__label">
                                {activeParentComment ? 'Replying to' : 'New Comment'}
                            </div>
                            <div className="ld-modal__title">
                                {activeParentComment
                                    ? activeParentComment.user?.name
                                    : discussion?.title}
                            </div>
                        </div>
                        <button className="ld-modal__close" onClick={closeCommentModal} aria-label="Close">
                            <FaTimes />
                        </button>
                    </div>

                    <div className="ld-modal__body">
                        {commentError && (
                            <div className="alert alert-danger py-2 mb-3 small">{commentError}</div>
                        )}

                        {/* Quoted parent comment when replying */}
                        {activeParentComment && (
                            <div className="ld-modal__quote">
                                <div className="ld-modal__quote-author">{activeParentComment.user?.name}</div>
                                <div className="ld-modal__quote-body">{activeParentComment.body}</div>
                            </div>
                        )}

                        {/* Textarea + mention dropdown */}
                        <div className="position-relative">
                            <Form.Control
                                as="textarea"
                                rows={4}
                                className="ld-modal__textarea"
                                placeholder="Write your comment… Use @ to mention someone"
                                value={commentBody}
                                onChange={handleCommentBodyChange}
                                ref={textareaRef}
                            />
                            <div className="ld-modal__char-count">
                                {commentBody.length} chars
                            </div>
                            {filteredListeners.length > 0 && (
                                <div className="mention-dropdown border bg-white shadow-sm position-absolute w-100"
                                    style={{ zIndex: 1050, top: '100%', left: 0 }}>
                                    {filteredListeners.map((l) => (
                                        <div key={l.guid} className="mention-option px-3 py-2"
                                            onMouseDown={(e) => { e.preventDefault(); handleSelectMention(l); }}>
                                            {l.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Mention tags */}
                        {commentMentions.length > 0 && (
                            <div className="ld-modal__mentions">
                                <span className="ld-modal__mentions-label">Mentioning:</span>
                                {commentMentions.map((g) => {
                                    const l = listeners.find((x) => x.guid === g);
                                    return l ? (
                                        <span key={g} className="ld-modal__mention-tag">
                                            @{l.name}
                                            <button
                                                className="ld-modal__mention-remove"
                                                onClick={() => setCommentMentions((prev) => prev.filter((x) => x !== g))}
                                                aria-label={`Remove @${l.name}`}
                                            >
                                                <FaTimes />
                                            </button>
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="ld-modal__footer">
                        <span className="ld-modal__hint">
                            Use @ to mention · mentioned users are notified
                        </span>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm"
                                onClick={closeCommentModal} disabled={submittingComment}>
                                Cancel
                            </Button>
                            <Button variant="primary" size="sm"
                                onClick={handleSubmitComment}
                                disabled={submittingComment || !commentBody.trim()}>
                                {submittingComment ? 'Posting…' : 'Post Comment'}
                            </Button>
                        </div>
                    </div>

                </Modal.Body>
            </Modal>
        </LoadingFade>
    );
};

export default ListenerDiscussion;
